import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/button";
import { SEO } from "@/components/SEO";

const CookiePolicy = () => {
  return (
    <>
      <SEO 
        title="Cookie Policy"
        description="Understand how sksenq.com uses cookies and similar technologies. We use only essential cookies for security and preference storage."
        canonical="/cookie-policy"
      />
      <div className="min-h-screen bg-background">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-12 max-w-4xl">
        <Link to="/">
          <Button variant="ghost" className="mb-8 gap-2">
            <ArrowLeft size={16} />
            Back to Home
          </Button>
        </Link>

        <article className="prose prose-lg dark:prose-invert max-w-none">
          <h1 className="text-4xl font-bold mb-2">Cookie Policy</h1>
          <p className="text-muted-foreground mb-8">Last updated: 28 December 2025</p>

          <p>
            This Cookie Policy explains how Sksenq LLC ("we", "us", or "our") uses cookies and similar technologies when you visit our website https://sksenq.com (the "Website").
          </p>

          <h2 className="text-2xl font-semibold mt-10 mb-4">1. What Are Cookies?</h2>
          <p>
            Cookies are small text files that are stored on your device (computer, tablet, or mobile) when you visit a website. They are widely used to make websites work more efficiently and to provide information to website owners.
          </p>

          <h2 className="text-2xl font-semibold mt-10 mb-4">2. How We Use Cookies</h2>
          <p>We use cookies and similar technologies for the following purposes:</p>
          
          <h3 className="text-xl font-semibold mt-6 mb-3">Essential Cookies</h3>
          <p>
            These cookies are necessary for the Website to function properly. They enable core functionality such as security, network management, and accessibility. You cannot opt out of these cookies as the Website would not function properly without them.
          </p>
          <ul>
            <li><strong>Cookie consent:</strong> Stores your cookie preferences</li>
            <li><strong>Security:</strong> Helps protect against fraudulent activity and spam</li>
            <li><strong>Session management:</strong> Maintains your session state during your visit</li>
          </ul>

          <h3 className="text-xl font-semibold mt-6 mb-3">Preference Cookies</h3>
          <p>
            These cookies allow the Website to remember choices you make (such as your preferred theme - light or dark mode) and provide enhanced, personalized features.
          </p>
          <ul>
            <li><strong>Theme preference:</strong> Remembers your preferred color scheme (light/dark mode)</li>
          </ul>

          <h2 className="text-2xl font-semibold mt-10 mb-4">3. Cookies We Do Not Use</h2>
          <p>We do not use:</p>
          <ul>
            <li>Analytics or tracking cookies</li>
            <li>Advertising or marketing cookies</li>
            <li>Third-party cookies for profiling or behavioral targeting</li>
            <li>Social media tracking cookies</li>
          </ul>

          <h2 className="text-2xl font-semibold mt-10 mb-4">4. Third-Party Services</h2>
          <p>
            Our Website uses AWS services for backend processing. These services do not set cookies on your device.
          </p>

          <h2 className="text-2xl font-semibold mt-10 mb-4">5. Your Cookie Choices</h2>
          <p>
            When you first visit our Website, you will be presented with a cookie consent banner that allows you to accept or decline non-essential cookies.
          </p>
          <p>You can also control cookies through your browser settings:</p>
          <ul>
            <li><strong>Chrome:</strong> Settings → Privacy and security → Cookies</li>
            <li><strong>Firefox:</strong> Settings → Privacy & Security → Cookies</li>
            <li><strong>Safari:</strong> Preferences → Privacy → Cookies</li>
            <li><strong>Edge:</strong> Settings → Cookies and site permissions</li>
          </ul>
          <p>
            Please note that blocking essential cookies may affect the functionality of the Website.
          </p>

          <h2 className="text-2xl font-semibold mt-10 mb-4">6. Local Storage</h2>
          <p>
            In addition to cookies, we may use local storage to store your preferences (such as theme settings). Local storage works similarly to cookies but is stored differently in your browser. You can clear local storage through your browser's developer tools or settings.
          </p>

          <h2 className="text-2xl font-semibold mt-10 mb-4">7. Cookie Retention</h2>
          <p>The cookies we use have varying lifespans:</p>
          <ul>
            <li><strong>Session cookies:</strong> Deleted when you close your browser</li>
            <li><strong>Persistent cookies:</strong> Remain on your device for a set period (typically up to 1 year for preference cookies)</li>
            <li><strong>Consent cookie:</strong> Stored for 1 year to remember your cookie preferences</li>
          </ul>

          <h2 className="text-2xl font-semibold mt-10 mb-4">8. Updates to This Policy</h2>
          <p>
            We may update this Cookie Policy from time to time to reflect changes in technology, legislation, or our data practices. Any updates will be posted on this page with a revised "Last updated" date.
          </p>

          <h2 className="text-2xl font-semibold mt-10 mb-4">9. Contact Information</h2>
          <p>If you have questions about our use of cookies, please contact:</p>
          <ul className="list-none pl-0">
            <li><a href="mailto:privacy@sksenq.com" className="text-primary hover:underline">privacy@sksenq.com</a></li>
            <li><a href="mailto:contact@sksenq.com" className="text-primary hover:underline">contact@sksenq.com</a></li>
          </ul>

          <h2 className="text-2xl font-semibold mt-10 mb-4">10. Related Policies</h2>
          <p>For more information about how we handle your data, please also review:</p>
          <ul>
            <li><Link to="/privacy-policy" className="text-primary hover:underline">Privacy Policy</Link></li>
            <li><Link to="/terms-of-service" className="text-primary hover:underline">Terms of Service</Link></li>
          </ul>
        </article>
      </div>
      </div>
    </>
  );
};

export default CookiePolicy;
